import React, { useMemo, useRef } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLRouteParts } from '../../../../routing/sml/SMLRoutes';
import { SMLLink } from '../SMLLink/SMLLink';
import { useOverflowFade } from './useOverflowFade';
import styles from './SMLSectionTabs.module.scss';

/** One tab of a page with tabs (Insights, Posts, Pitch, Charts). Each tab is its own route. */
export interface SMLSectionTab {
  id: string;
  label: MessageDescriptor;
  route: SMLRouteParts;
  /** Query values the tab link always sets. */
  query?: Record<string, string>;
  active: boolean;
  /** A red count of things that wait for the person (design navigation-v2 c: failed posts). */
  badge?: number;
}

interface SMLSectionTabsProps {
  tabs: SMLSectionTab[];
  /** The name of the tab row for assistive tech ("Insights"). */
  ariaLabel: MessageDescriptor;
  /**
   * The query values a tab change keeps. Default: the period of the page (`?days=`). Charts keeps
   * its date picker (`?date=&rangeDays=`).
   */
  keepQuery?: readonly string[];
}

const DEFAULT_KEPT_QUERY: readonly string[] = ['days'];

/**
 * The page tabs of navigation v2 (design `.tabs2`): links under the page title, the active tab
 * underlined, a red count when a tab holds something that waits. Each tab is a route, so a tab
 * change keeps the address shareable; the period (`?days=`, or the `keepQuery` values) goes with it. The row scrolls sideways
 * and fades at the right edge when it does not fit (phones, long Pitch and Charts rows).
 */
const SMLSectionTabsComponent: React.FC<SMLSectionTabsProps> = ({
  tabs,
  ariaLabel,
  keepQuery = DEFAULT_KEPT_QUERY,
}) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const rowRef = useRef<HTMLDivElement>(null);
  const fadeEnd = useOverflowFade(rowRef);

  const kept = useMemo(() => {
    const values: Record<string, string> = {};
    for (const key of keepQuery) {
      const value = searchParams.get(key);
      if (value !== null && value.length > 0) {
        values[key] = value;
      }
    }
    return values;
  }, [searchParams, keepQuery]);

  return (
    <nav className={styles.tabs} aria-label={intl.formatMessage(ariaLabel)}>
      <div ref={rowRef} className={clsx(styles.row, fadeEnd && styles.fadeEnd)}>
        {tabs.map((tab) => {
          const params = { ...kept, ...tab.query };
          const hasBadge = tab.badge !== undefined && tab.badge > 0;
          return (
            <SMLLink
              key={tab.id}
              to={tab.route}
              params={Object.keys(params).length > 0 ? params : undefined}
              className={clsx(styles.tab, tab.active && styles.on)}
              ariaCurrent={tab.active ? 'page' : undefined}
            >
              {intl.formatMessage(tab.label)}
              {hasBadge && (
                <span className={styles.badge} data-testid={`section-tab-badge-${tab.id}`}>
                  {intl.formatNumber(tab.badge ?? 0)}
                </span>
              )}
            </SMLLink>
          );
        })}
      </div>
    </nav>
  );
};

export const SMLSectionTabs = TypedMemo(SMLSectionTabsComponent);
